import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1";
import { useIdentity } from "../../auth/useIdentity";
import { decodeAccessTokenClaims, zitadelClaimKeys, type Claims } from "../../auth/namespace";
import { flagEnabled } from "../../config";
import { ApiError } from "../../api/errors";
import { fetchSummary, greetingFor, formatCount, formatInr, EXPIRING_WINDOW_DAYS, type Summary } from "../../api/welcome";
import { canCreateSubscriber, CREATE_SUBSCRIBER_PATH, isApplicantOnly, isSponsorOnly, PROVIDERS_IN_SIDEBAR, showsPlatformWelcome } from "../../components/nav";
import { PlatformWelcome } from "./PlatformWelcome";
import { OperatorTypeQuestion } from "./OperatorTypeQuestion";
import { OnboardingSteps } from "./OnboardingSteps";
import { SetupNotices } from "./SetupNotices";
import { PlanCard } from "./PlanCard";
import { useProviders, type ProviderView } from "../providers/useProviders";
import { shortName } from "../../auth/scope";
import { useNavigate } from "react-router-dom";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { fetchTraffic, formatMB, nasStatus, type NASTraffic } from "../../api/traffic";

/**
 * The Welcome Page: what an operator lands on after signing in. An
 * operational overview, not a dashboard -- the owner's word, and the brief's:
 * a few numbers that say what state the tenant is in right now, each one a
 * door into the report it is counted from.
 *
 * ⛔ No infrastructure / bandwidth section yet. Nothing in the estate serves
 * per-tenant bandwidth (measured 2026-09-14: Prometheus is scraped, not
 * proxied; radacct octets are summed per customer only). The section on the
 * approved canvas waits for a metrics route rather than shipping with made-up
 * numbers or an empty card.
 */

const DATE = new Intl.DateTimeFormat("en-IN", { weekday: "long", day: "numeric", month: "long" });

function reason(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.kind === "forbidden") return "Refused: your role cannot read this.";
    if (e.kind === "not-found") return "Not available on this gateway yet.";
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

/** One number, one label, one door. The same anatomy for every card. */
function StatCard({
  label,
  hint,
  to,
  value,
  state,
}: {
  label: string;
  hint: string;
  to?: string | undefined;
  value: number | undefined;
  state: { isLoading: boolean; error: unknown };
}) {
  const body = state.isLoading ? (
    <Skeleton variant="text" width={72} sx={{ fontSize: 34 }} />
  ) : state.error ? (
    <Tooltip title={reason(state.error)}>
      <Typography component="span" variant="body2" color="text.secondary" sx={{ lineHeight: "34px" }}>
        unavailable
      </Typography>
    </Tooltip>
  ) : (
    <Typography component="span" variant="h4" sx={{ lineHeight: 1, letterSpacing: "-0.02em" }} data-testid={`stat-${label}`}>
      {formatCount(value ?? 0)}
    </Typography>
  );
  return (
    <Box
      component={to ? RouterLink : "div"}
      {...(to ? { to } : {})}
      sx={{
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: "12px",
        p: 2.5,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minWidth: 0,
        textDecoration: "none",
        color: "text.primary",
        ...(to ? { "&:hover": { borderColor: "text.disabled" } } : {}),
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      {body}
      <Typography variant="caption" color="text.disabled">
        {hint}
      </Typography>
    </Box>
  );
}

function RevenueCard({ now, summary, state }: { now: Date; summary: Summary | undefined; state: { isLoading: boolean; error: unknown } }) {
  const q = { data: summary?.revenue, ...state };
  const periods = [
    { label: "Today", key: "today" as const, note: q.data ? `${q.data.count} recharge${q.data.count === 1 ? "" : "s"} this month` : "" },
    { label: "This week", key: "week" as const, note: "from Monday" },
    { label: "This month", key: "month" as const, note: `from 1 ${now.toLocaleDateString("en-IN", { month: "long" })}` },
  ];
  const max = q.data?.month || 1;
  return (
    <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: "12px", p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
        <Typography variant="h6" sx={{ fontSize: 16 }}>
          Revenue collected
        </Typography>
        <Typography variant="caption" color="text.disabled">
          successful recharges
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button component={RouterLink} to="/reports/recharge-log" size="small" sx={{ color: "brand.link" }}>
          Recharge log
        </Button>
      </Box>
      {q.error ? (
        <Typography variant="body2" color="text.secondary">
          {reason(q.error)}
        </Typography>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 3 }}>
          {periods.map((p) => (
            <Box key={p.key} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {p.label}
              </Typography>
              {q.isLoading ? (
                <Skeleton variant="text" width={96} sx={{ fontSize: 28 }} />
              ) : (
                <Typography variant="h5" sx={{ lineHeight: 1, letterSpacing: "-0.02em" }} data-testid={`revenue-${p.key}`}>
                  {formatInr(q.data?.[p.key] ?? 0)}
                </Typography>
              )}
              {/* One scale for all three bars, so a period reads as a share of the month. */}
              <Box sx={{ height: 6, borderRadius: 999, bgcolor: "brand.surface2", overflow: "hidden" }}>
                <Box sx={{ height: "100%", borderRadius: 999, bgcolor: "text.primary", width: `${Math.min(100, ((q.data?.[p.key] ?? 0) / max) * 100)}%` }} />
              </Box>
              <Typography variant="caption" color="text.disabled">
                {p.note}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * One provider's numbers, from the same summary route asked for ITS
 * namespace -- the console's token may act on any of its descendants.
 */
function ProviderCard({ p, token, onOpen }: { p: ProviderView; token: string | null; onOpen: () => void }) {
  const q = useQuery({
    queryKey: ["welcome", "summary", p.namespace],
    enabled: Boolean(token),
    queryFn: ({ signal }) => fetchSummary({ token, namespace: p.namespace }, signal),
  });
  const n = (v: number | undefined) => (q.isLoading ? "…" : q.error ? "—" : formatCount(v ?? 0));
  return (
    <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: "12px", p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }} data-testid={`provider-${p.namespace}`}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontSize: 18, lineHeight: 1.2 }}>
            {p.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
            {p.namespace}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Chip size="small" label={p.active ? "Active" : "Inactive"} color={p.active ? "success" : "default"} variant="outlined" />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1.5 }}>
        {[
          ["Active users", n(q.data?.active_users)],
          ["Sessions", n(q.data?.active_sessions)],
          ["Expiring · 5 d", n(q.data?.expiring_soon)],
          ["Revenue today", q.isLoading ? "…" : q.error ? "—" : formatInr(q.data?.revenue.today ?? 0)],
        ].map(([l, v]) => (
          <Box key={l} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {l}
            </Typography>
            <Typography variant="h6" sx={{ lineHeight: 1 }}>
              {v}
            </Typography>
          </Box>
        ))}
      </Box>
      {q.error ? (
        <Typography variant="caption" color="text.secondary">
          {reason(q.error)}
        </Typography>
      ) : null}
      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1, borderTop: 1, borderColor: "divider" }}>
        <Button size="small" variant="outlined" onClick={onOpen}>
          Open console
        </Button>
      </Box>
    </Box>
  );
}

const STATUS_LABEL = { online: "Online", idle: "Idle", quiet: "Quiet" } as const;

/**
 * The infrastructure ledger (Option A on the approved canvas), from RADIUS
 * accounting: one row per NAS, today. No sparkline and no Mbps -- see
 * api/traffic.ts for why -- and it says so under the title.
 */
function TrafficCard({ identity, consolidated }: { identity: ReturnType<typeof useIdentity>; consolidated: boolean }) {
  const q = useQuery({
    queryKey: ["welcome", "traffic", identity.namespace],
    enabled: Boolean(identity.token && identity.namespace),
    queryFn: ({ signal }) => fetchTraffic(identity, 1, signal),
  });
  const rows: NASTraffic[] = q.data?.nas ?? [];
  const cell = { py: 1, fontSize: 13 } as const;
  return (
    <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: "12px", p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }} data-testid="traffic">
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, flexWrap: "wrap" }}>
        <Typography variant="h6" sx={{ fontSize: 16 }}>
          Infrastructure
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {q.data ? `${rows.length} NAS heard from today · from RADIUS accounting` : "from RADIUS accounting"}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {q.data && (
          <Typography variant="body2" color="text.secondary" data-testid="traffic-totals">
            <strong>{formatCount(q.data.online_now)}</strong> online · <strong>{formatMB(q.data.in_mb)}</strong> in · <strong>{formatMB(q.data.out_mb)}</strong> out today
          </Typography>
        )}
      </Box>
      {q.isLoading ? (
        <Skeleton variant="rectangular" height={72} />
      ) : q.error ? (
        <Typography variant="body2" color="text.secondary">
          {reason(q.error)}
        </Typography>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No RADIUS accounting from this namespace today. A NAS appears here once a session has been accounted for it.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {consolidated && <TableCell sx={cell}>Provider</TableCell>}
                <TableCell sx={cell}>NAS</TableCell>
                <TableCell sx={cell}>IP address</TableCell>
                <TableCell sx={cell} align="right">
                  Online now
                </TableCell>
                <TableCell sx={cell} align="right">
                  Sessions today
                </TableCell>
                <TableCell sx={cell} align="right">
                  In / out today
                </TableCell>
                <TableCell sx={cell}>Last heard</TableCell>
                <TableCell sx={cell}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((n) => {
                const st = nasStatus(n);
                return (
                  <TableRow key={`${n.namespace}/${n.nas_ip}`} hover data-testid={`nas-${n.nas_ip}`}>
                    {consolidated && <TableCell sx={cell}>{shortName(n.namespace)}</TableCell>}
                    <TableCell sx={{ ...cell, fontWeight: 600 }}>{n.nas_name || n.nas_ip}</TableCell>
                    <TableCell sx={{ ...cell, fontFamily: "monospace" }}>{n.nas_ip}</TableCell>
                    <TableCell sx={cell} align="right">
                      {formatCount(n.online_now)}
                    </TableCell>
                    <TableCell sx={cell} align="right">
                      {formatCount(n.sessions)}
                    </TableCell>
                    <TableCell sx={cell} align="right">
                      {formatMB(n.in_mb)} / {formatMB(n.out_mb)}
                    </TableCell>
                    <TableCell sx={cell}>{n.last_update.slice(11, 16) || n.last_update}</TableCell>
                    <TableCell sx={cell}>
                      <Chip size="small" variant="outlined" label={STATUS_LABEL[st]} color={st === "online" ? "success" : "default"} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.disabled">
        Bytes are what each session reported; accounting has no time series, so there is no rate here.
      </Typography>
    </Box>
  );
}

export function Welcome() {
  const identity = useIdentity();
  // io.ankhos is the platform, not an operator: it gets its own page.
  if (showsPlatformWelcome(flagEnabled, identity.roles, identity.namespace, Boolean(identity.provider))) {
    return <PlatformWelcome />;
  }
  return <OperatorWelcome />;
}

const holdsOwnerRole = (roles: string[]) => roles.some((r) => ["operator", "manager", "superadmin"].includes(r.toLowerCase()));

function OperatorWelcome() {
  const identity = useIdentity();
  const auth = useAuth();
  const navigate = useNavigate();
  const providers = useProviders();
  // At the operator's own namespace with providers beneath it, the numbers
  // cover the whole subtree (the summary route scopes by prefix) -- say so.
  const consolidated = !identity.provider && providers.direct.length > 0;
  const whoAmI = identity.provider
    ? (providers.inside?.name ?? identity.provider)
    : identity.rootNamespace
      ? shortName(identity.rootNamespace) + (providers.all.length ? ` and its ${providers.all.length} provider${providers.all.length === 1 ? "" : "s"}` : "")
      : "";
  // One "now" per visit: the report windows are built from it, and a fresh
  // Date on every render would make every render a different request.
  const [now] = useState(() => new Date());
  const sponsor = isSponsorOnly(identity.roles);
  // An applicant reaches no report yet; it is asked what kind of operator it is.
  const applicant = isApplicantOnly(identity.roles);
  const ready = Boolean(identity.token && identity.namespace) && !sponsor && !applicant;

  // Token diagnostics, kept from the first console screen: a wrong token is
  // the failure an operator hits first, and this is the page they hit it on.
  const idKeys = zitadelClaimKeys(auth.user?.profile as Claims | undefined);
  const accessClaims = decodeAccessTokenClaims(auth.user?.access_token);
  const accessIsJwt = accessClaims !== null;

  // One call answers every number (radreportsapi /reports/summary). A card
  // links to its report only when that report's screen is switched on.
  const q = useQuery({
    queryKey: ["welcome", "summary", identity.namespace, now.toDateString()],
    enabled: ready,
    queryFn: ({ signal }) => fetchSummary(identity, signal),
  });
  const state = { isLoading: q.isLoading, error: q.error };
  const link = (flag: string, to: string) => (flagEnabled(flag) ? to : undefined);

  const cards = [
    <StatCard key="plans" label="Active plans" hint="in the catalogue" to={link("reports.plans", "/reports/plans")} value={q.data?.active_plans} state={state} />,
    <StatCard key="users" label="Active users" hint="subscriptions not yet ended" to={link("reports.activePlans", "/reports/active-plans")} value={q.data?.active_users} state={state} />,
    <StatCard key="sessions" label="Active sessions" hint="online right now" to={link("reports.activeSessions", "/reports/active-sessions")} value={q.data?.active_sessions} state={state} />,
    <StatCard
      key="expiring"
      label={`Expiring in ${EXPIRING_WINDOW_DAYS} days`}
      hint="subscriptions ending soon"
      to={link("reports.expiringPlans", "/reports/expiring-plans")}
      value={q.data?.expiring_soon}
      state={state}
    />,
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ letterSpacing: "-0.01em" }}>
            {greetingFor(now.getHours())}
            {identity.firstName ? `, ${identity.firstName}` : ""}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {DATE.format(now)}
            {!applicant && (
              <>
                {" · "}
                {whoAmI && <>{whoAmI} · </>}
                <code>{identity.namespace ?? "no namespace"}</code>
              </>
            )}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {/* 🎨 The one amber element on this screen. */}
        {!sponsor && !applicant && flagEnabled("users.create") && canCreateSubscriber(identity.roles) && (
          <Button component={RouterLink} to={CREATE_SUBSCRIBER_PATH} variant="contained" color="secondary" startIcon={<PersonAddIcon />}>
            Create subscriber
          </Button>
        )}
      </Box>

      {!identity.namespace && (
        <Alert severity="error">
          Your token carries no namespace. Every request will be refused locally before it is sent.
          Check the SPA client's scope set — <code>urn:zitadel:iam:user:metadata</code> must be
          requested.
        </Alert>
      )}
      {!accessIsJwt && (
        <Alert severity="error">
          <strong>The access token is opaque, so no API call can succeed.</strong> The gateway
          validates the bearer token locally against ZITADEL&apos;s JWKS — it parses it as a JWT and
          has no introspection path. Fix in ZITADEL on the <code>AnkhOS Web</code> application, under
          Token Settings: set <strong>Auth Token Type</strong> to <code>JWT</code> and tick{" "}
          <strong>Add user roles to the access token</strong>.
        </Alert>
      )}
      {identity.namespace && identity.roles.length === 0 && (
        <Alert severity="warning">
          Your token carries a namespace but <strong>no role</strong>, so every report will return
          403. The read-only grant is the issuer role <code>support</code>.
          <Typography variant="caption" component="div" sx={{ mt: 1, fontFamily: "monospace" }}>
            ID token: {idKeys.length ? idKeys.join(", ") : "(no urn:zitadel claims)"}
          </Typography>
        </Alert>
      )}

      {!applicant && !sponsor && holdsOwnerRole(identity.roles) && <SetupNotices activePlans={q.data?.active_plans} />}
      {!applicant && !sponsor && holdsOwnerRole(identity.roles) && <PlanCard />}
      {applicant && <OperatorTypeQuestion />}
      {applicant && <OnboardingSteps />}

      {sponsor && (
        <Alert severity="info">
          Your sign-in manages an organisation&apos;s member roster. Open <strong>Member roster</strong>{" "}
          in the menu to upload or review members.
        </Alert>
      )}

      {ready && consolidated && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} data-testid="consolidated">
          <Chip size="small" label="Consolidated" sx={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase" }} />
          <Typography variant="caption" color="text.secondary">
            {shortName(identity.rootNamespace ?? "")} and every provider beneath it
          </Typography>
        </Box>
      )}
      {ready && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 2 }}>
          {cards}
        </Box>
      )}

      {ready && <RevenueCard now={now} summary={q.data} state={state} />}

      {ready && <TrafficCard identity={identity} consolidated={consolidated} />}

      {ready && providers.direct.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
            <Typography variant="h6" component="h2" sx={{ fontSize: 18 }}>
              Providers
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {providers.direct.length} under {identity.provider ? (providers.inside?.name ?? identity.provider) : shortName(identity.rootNamespace ?? "")}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            {providers.direct.length > PROVIDERS_IN_SIDEBAR && (
              <Button component={RouterLink} to="/providers" size="small" sx={{ color: "brand.link" }}>
                All {providers.direct.length} providers
              </Button>
            )}
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
            {providers.direct.slice(0, PROVIDERS_IN_SIDEBAR).map((p) => (
              <ProviderCard
                key={p.namespace}
                p={p}
                token={identity.token}
                onOpen={() => {
                  identity.enter(p.namespace);
                  navigate("/");
                }}
              />
            ))}
          </Box>
        </Box>
      )}

    </Box>
  );
}
